import React from "react";
import PropTypes from "prop-types";
import { Box, Typography, CircularProgress, Chip, Button } from "@mui/material";
import PgHomeSectionPaper from "./PgHomeSectionPaper";
import { formatRelativeTime, getBondUpdateUrl } from "../../../globals/utils/homeHelpers";

/** Unassigned paralegal bonds panel (internal-user home). */
export default function IuHomeAssignmentSection({
  assignmentCopy,
  bonds,
  loadingUnassigned,
}) {
  return (
    <PgHomeSectionPaper sx={{ flex: "0 0 auto" }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Box>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="h6" sx={{ fontWeight: 500, mb: 0.25, color: "#333333" }}>
              {assignmentCopy.title}
            </Typography>
            <Chip size="small" color="error" label={bonds.length} />
          </Box>
          <Typography variant="body2" sx={{ color: "#6b7280", mb: 2 }}>
            {assignmentCopy.subtitle}
          </Typography>
        </Box>
        {loadingUnassigned && <CircularProgress size={18} />}
      </Box>
      <Box sx={{ flex: 1, overflowY: "auto", pr: 0.5, minHeight: 0, maxHeight: 320 }}>
        {bonds.length === 0 ? (
          <Typography variant="body2" sx={{ color: "text.secondary", fontSize: "0.75rem" }}>
            {assignmentCopy.emptyList}
          </Typography>
        ) : (
          bonds.map((bond) => (
            <Box
              key={bond.bond_id}
              sx={{
                p: 1,
                mb: 1,
                borderRadius: 1,
                bgcolor: (theme) =>
                  theme.palette.mode === "light" ? "#ffffff" : "background.default",
                border: "1px solid rgba(37, 99, 235, 0.12)",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 500, color: "text.primary", noWrap: true, mb: 1 }}
                >
                  {bond.bond_number
                    ? `${bond.full_name || "\u2014"} - ${bond.bond_number}`
                    : bond.full_name || "\u2014"}
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>
                  {bond.bond_type && `${bond.bond_type}`}
                  {bond.bond_type && bond.law_firm_name && ` \u2022 `}
                  {bond.law_firm_name && `${bond.law_firm_name}`}
                  {bond.status && ` \u2022 ${bond.status}`}
                  {bond.created_at && ` \u2022 ${formatRelativeTime(bond.created_at)}`}
                </Typography>
              </Box>
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  const url = getBondUpdateUrl(bond.bond_type, bond.bond_id);
                  if (url) window.open(url, "_blank");
                }}
                sx={{ textTransform: "none", fontSize: "0.7rem", px: 1, py: 0.5 }}
              >
                {assignmentCopy.view}
              </Button>
            </Box>
          ))
        )}
      </Box>
    </PgHomeSectionPaper>
  );
}

IuHomeAssignmentSection.propTypes = {
  assignmentCopy: PropTypes.shape({
    title: PropTypes.string,
    subtitle: PropTypes.string,
    emptyList: PropTypes.string,
    view: PropTypes.string,
  }).isRequired,
  bonds: PropTypes.arrayOf(PropTypes.object).isRequired,
  loadingUnassigned: PropTypes.bool.isRequired,
};
